import {Component, EventEmitter, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder} from '@angular/forms';
import {ServersDialogInf} from '../../servers/servers-dialog/servers-dialog.component';
import * as _ from 'lodash';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {Papa, ParseConfig} from 'ngx-papaparse';
import languageEncoding from 'detect-file-encoding-and-language';
import {FileRepositoryApiService} from '../../../services/api/users/file-repository/file-repository-api.service';

export interface FileRepositoryDialogInf {
  selectedItem: any;
  isEditMode?: boolean;
  normalizationGroupsOptions: Array<any>;
  attributes: Array<any>;
  type?: string;
}

@Component({
  selector: 'file-repository-dialog',
  templateUrl: './file-repository-dialog.component.html',
  styleUrls: ['./file-repository-dialog.component.scss']
})
export class FileRepositoryDialogComponent implements OnInit {

  onApply = new EventEmitter();
  onCancel = new EventEmitter();

  originalDicAttrMapping: any[];
  fileAttributesNames: string[];
  fileAttributesLocations: any[];
  fileUniquePropOptions: any[];
  fileDelimiters = [',', ';', '|'];
  isFileMappingOptionsReady: boolean;
  fileUploadSize = 0;
  fileRepositoryMaxSize = 1073741274; // 1GB
  fileEncodingWarning = false;
  testConnectionString = '';
  validateFileString = '';
  fileNameWithoutformat = '';
  azureBlobWithoutformat = '';
  selectedUniquePropertyText = '';


  actionFileTypesOptions = [
    {label: 'Add and Update Users' , key: 'AddAndUpdateUsers'},
    {label: 'Delete Users'         , key: 'DeleteUsers'},
    {label: 'Full Sync All Users'  , key: 'FullSyncAllUsers'}
  ];
  schedulingSourceTypeOptions: any[] = [
    { value: 'NO_SCHEDULING', text: 'No scheduling' },
    { value: 'LOCAL_FILE_PATH', text: 'Scheduled update from local file' },
    { value: 'AZURE_FILE_STORAGE', text: 'Scheduled update from Azure file storage' }
  ];

  readonly nameProp = {
    dicAttrId: -1,
    dicAttrName: 'Name',
    fileRepositoryAttrName: null,
    fileRepositoryAttrLocation: null,
    normalizationGroupId: null
  };

  azureStorageDetails: { connectionString: any, accountKey: any, container: any, blob: any } = { connectionString: null, accountKey: null, container: null, blob: null };

  constructor(public dialogRef: MatDialogRef<FileRepositoryDialogInf>,
              @Inject(MAT_DIALOG_DATA) public data: ServersDialogInf,
              private papa: Papa,
              private fileRepositoryApiService: FileRepositoryApiService,
              private formBuilder: FormBuilder){
  }

  ngOnInit(): void {
    this.fileAttributesLocations = _.range(51).map(key => ({text: key + 1, value: key}));
    this.setUniquePropOptions(this.data.attributes);
    if (!this.data.selectedItem.fileRepositorySyncSourceType) {
      this.initSchdulingSettings();
    }
    if (!this.data.isEditMode) {
      this.initializeAttributeMappingsForAdd();

    } else {
      this.setFileMappingOptionsForEditMode();
      this.setSelectedUniquePropertyText();
    }
    if (this.data.selectedItem.localFileName !== null) {
      this.fileNameWithoutformat = this.data.selectedItem.localFileName.replace('.csv', '');
    }
    if (this.data.selectedItem.azureStorageBlob !== null) {
      this.azureBlobWithoutformat = this.data.selectedItem.azureStorageBlob.replace('.csv', '');
    }
  }

  initializeAttributeMappingsForAdd(): void {
    this.data.selectedItem.dicAttrMapping =  this.data.attributes
      .filter((attribute) => {
        return !attribute.isCombined;
      })
      .map((attribute) => {
        return {
          dicAttrId: attribute.id,
          dicAttrName: attribute.name,
          fileRepositoryAttrName: null,
          fileRepositoryAttrLocation: null,
          normalizationGroupId: null
        };
      });

    this.data.selectedItem.dicAttrMapping.unshift(this.nameProp);
  }

  private setFileMappingOptionsForEditMode(): void {
    if (this.data.selectedItem.isFileHasHeaders) {
      this.fileAttributesNames = this.data.selectedItem.dicAttrMapping.map(prop => prop.fileRepositoryAttrName);
    }
    this.originalDicAttrMapping = _.cloneDeep(this.data.selectedItem.dicAttrMapping); // backup original mappings
  }

  private setUniquePropOptions(properties): void {
    this.fileUniquePropOptions = [];
    properties.forEach(prop => {
      this.fileUniquePropOptions.push({text: prop.name, value: prop.id});
    });

    this.fileUniquePropOptions.unshift({text: 'Name', value: -1});
  }

  onTabChanged(tabChangeEvent: MatTabChangeEvent): void {
    if (tabChangeEvent.index === 1 && this.data.selectedItem.file){
      this.setFileMappingOptions(this.data.selectedItem.file);
    }
  }

  cancel(): any {
    this.dialogRef.close();
    this.onCancel.emit();
  }

  submit(): any {
    this.omitMappingDoesntExistInFileForEdit();
    this.onApply.emit(this.data.selectedItem);
  }

  isFileSizeValid(): boolean {
    return this.fileUploadSize <= this.fileRepositoryMaxSize;
  }

  onFileChange(files): void {
    if (this.data.isEditMode) {
      this.initializeAttributeMappingsForAdd(); // init mappings for new file
    }
    const file = files[0];

    languageEncoding(file).then((fileInfo) => {
      if (!_.isNil(fileInfo) && (fileInfo.encoding !== 'UTF-8')) {
        this.fileEncodingWarning = true;
      } else {
        this.fileEncodingWarning = false;
      }
    });

    this.isFileMappingOptionsReady = false;
    if (files && files.length === 1) {
      this.clearMappingData();
      if (this.data.isEditMode)
      {
        this.syncDicAttrMappingsForOriginalMappingsAndNewFile(); // populate original mappings
      }
      this.data.selectedItem.file = files[0];
      this.fileUploadSize = files[0].size;
    }
  }

  syncDicAttrMappingsForOriginalMappingsAndNewFile(): void {
    this.data.selectedItem.dicAttrMapping.forEach((mapping, index) => {
      const mathcingMapping = this.originalDicAttrMapping.find(originalMapping => originalMapping.dicAttrName === mapping.dicAttrName);

      if (mathcingMapping) {
        // Merge mappings from oldDicAttrMapping into dicAttrMapping
        this.data.selectedItem.dicAttrMapping[index] = { ...mapping, ...mathcingMapping };
      }
      // If no match is found, ignore this item
    });
  }

  clearMappingData(): void {
    this.data.selectedItem.dicAttrMapping.forEach((attribute) => {
      attribute.fileRepositoryAttrName = null;
      attribute.fileRepositoryAttrLocation = null;
      attribute.normalizationGroupId = null;
    });
  }

  private setFileMappingOptions(file): void {
    if (this.data.selectedItem.isFileHasHeaders) {
      this.getFileHeaders(file);
    } else {
      this.isFileMappingOptionsReady = true;
    }
  }

  omitMappingDoesntExistInFileForEdit(): void {
    if (this.data.isEditMode) {
      if (this.data.selectedItem.isFileHasHeaders) {
        this.data.selectedItem.dicAttrMapping.forEach((mapping) => {
          if (!this.fileAttributesNames.includes(mapping.fileRepositoryAttrName)) {
            mapping.fileRepositoryAttrName = null;
          }
        });
      }
    }
  }

  private getFileHeaders(file): void {
    const config: ParseConfig = {
      delimiter: this.data.selectedItem.delimiter,
      preview: 1,
      step: (row, parser) => {
        if (row && row.data && row.data.length > 0) {
          this.fileAttributesNames = row.data;
          this.isFileMappingOptionsReady = true;
        } else {
          this.data.selectedItem.isFileHasHeaders = false;
          this.isFileMappingOptionsReady = true;
        }
        parser.abort();
      }
    };
    this.papa.parse(file, config);
  }

  initSchdulingSettings(): void {
    this.data.selectedItem.fileRepositorySyncSourceType = 'NO_SCHEDULING';
    this.data.selectedItem.fileRepositorySchedulingAction = 'AddAndUpdateUsers';
    this.data.selectedItem.syncIntervalHours = 24;
    this.data.selectedItem.queryTimeout = 120;
  }

  onLocalFileNameChange(): void {
    this.fileNameWithoutformat = this.data.selectedItem.localFileName.replace('.csv', '');
  }
  onAzureBlobNameChange(): void {
    this.azureBlobWithoutformat = this.data.selectedItem.azureStorageBlob.replace('.csv', '');
  }

  async verifyPath(): Promise<void> {
    const fileRepositoryJson = {
      id: 0,
      name: null,
      status: null,
      delimiter: null,
      isFileHasHeaders: null,
      uniquePropertyId: null,
      isNameUnique: null,
      nameFileMapping: null,
      nameFileLocation: null,
      numberOfUsers: null,
      lastTimestamp: null,
      description: null,
      fileRepositoryAction: 0,
      fileRepositorySyncSourceType: 0,
      localFileName: this.data.selectedItem.localFileName,
      azureStorageConnectionString: null,
      azureStorageAccountKey: null,
      azureStorageContainer: null,
      azureStorageBlob: null,
      fileRepositorySchedulingAction: 0,
      syncIntervalHours: null,
      queryTimeout: null,
      lastSync: null,
      dicAttrMapping: null,
      validateChecksum: this.data.selectedItem.validateChecksum
    };
    this.validateFileString = await this.fileRepositoryApiService.validateLocalFile(fileRepositoryJson);
  }

  async testAzureConnection(): Promise<void> {
    const fileRepositoryJson = {
      id: 0,
      name: null,
      status: null,
      delimiter: null,
      isFileHasHeaders: null,
      uniquePropertyId: null,
      isNameUnique: null,
      nameFileMapping: null,
      nameFileLocation: null,
      numberOfUsers: null,
      lastTimestamp: null,
      description: null,
      fileRepositoryAction: 0,
      fileRepositorySyncSourceType: 0,
      localFileName: null,
      azureStorageConnectionString: this.data.selectedItem.azureStorageConnectionString,
      azureStorageAccountKey: null,
      azureStorageContainer: this.data.selectedItem.azureStorageContainer,
      azureStorageBlob: this.data.selectedItem.azureStorageBlob,
      fileRepositorySchedulingAction: 0,
      syncIntervalHours: null,
      queryTimeout: this.data.selectedItem.queryTimeout,
      lastSync: null,
      dicAttrMapping: null,
      validateChecksum: this.data.selectedItem.validateChecksum
    };
    this.testConnectionString = await this.fileRepositoryApiService.testAzureStorageConnection(fileRepositoryJson);
  }
  setSelectedUniquePropertyText(): void {
    this.fileUniquePropOptions.forEach(option => {
      if (option.value === this.data.selectedItem.unique) {
        this.selectedUniquePropertyText = option.text;
      }
    });
  }
}
